import { PhotoElement, PhotoInfo } from '@dreambooks/core';
import { extractPhotoInterfacesFromTemplate, PhotoInterface } from '@lfm/image-engine';

/** Prefix used for placeholder photo md5 values */
export const COMPOSITION_PLACEHOLDER_PREFIX = 'comp_placeholder_';

/**
 * Generate a placeholder SVG data URL at specific dimensions.
 * Design: beige background (#d9d5d0), inset border (#c0bbb5), centered photo icon (#b5b0aa).
 * Generating at exact dimensions prevents crop-induced border clipping.
 */
export function generatePlaceholderSvg(w: number, h: number): string {
  const iconSize = 350;
  const iStroke = 10;

  const k = iconSize / 24;
  const ox = Math.round(w / 2 - iconSize / 2);
  const oy = Math.round(h / 2 - iconSize / 2);
  const iRx = Math.max(1, Math.round(2 * k));

  const rx = ox + Math.round(3 * k);
  const ry = oy + Math.round(3 * k);
  const rw = Math.round(18 * k);
  const rh = rw;
  const ccx = ox + Math.round(8.5 * k);
  const ccy = oy + Math.round(8.5 * k);
  const cr = Math.max(1, Math.round(1.5 * k));
  const p1x = ox + Math.round(21 * k);
  const p1y = oy + Math.round(15 * k);
  const p2x = ox + Math.round(16 * k);
  const p2y = oy + Math.round(10 * k);
  const p3x = ox + Math.round(5 * k);
  const p3y = oy + Math.round(21 * k);

  return 'data:image/svg+xml,' +
    `%3Csvg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'%3E` +
    `%3Crect width='${w}' height='${h}' fill='%23e1e1e1'/%3E` +
    `%3Cg fill='none' stroke='%23b5b0aa' stroke-width='${iStroke}' stroke-linecap='round' stroke-linejoin='round' opacity='0.6'%3E` +
    `%3Crect x='${rx}' y='${ry}' width='${rw}' height='${rh}' rx='${iRx}'/%3E` +
    `%3Ccircle cx='${ccx}' cy='${ccy}' r='${cr}'/%3E` +
    `%3Cpath d='M${p1x} ${p1y} L${p2x} ${p2y} L${p3x} ${p3y}'/%3E` +
    `%3C/g%3E%3C/svg%3E`;
}

/**
 * Strip photo-specific data (uid, photoId) from a BSP tree template,
 * replacing each leaf node with generic slot identifiers (slot_0, slot_1, ...).
 *
 * Input template leaf format:  "ratio desiredSize uid photoId"
 * Output template leaf format: "ratio desiredSize slot_0 slot_0"
 */
export function stripPhotosFromTemplate(template: string): { cleanTemplate: string; photoCount: number } {
  if (!template) return { cleanTemplate: '', photoCount: 0 };

  const photos = extractPhotoInterfacesFromTemplate(template);
  let cleanTemplate = template;

  photos.forEach((photo, index) => {
    const slotId = `slot_${index}`;
    // Match the exact "uid photoId" portion in the leaf node string
    // Leaf nodes look like: "0.75 1 rp_abc123 md5hash"
    // We need to replace "rp_abc123 md5hash" with "slot_N slot_N"
    const escapedUid = photo.uid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapedPhotoId = (photo.photoId || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`${escapedUid} ${escapedPhotoId}`);
    cleanTemplate = cleanTemplate.replace(pattern, `${slotId} ${slotId}`);
  });

  return { cleanTemplate, photoCount: photos.length };
}

/**
 * Reconstruct a template by replacing slot identifiers with actual photo data.
 * Photos are assigned by order: slot_0 → photos[0], slot_1 → photos[1], etc.
 *
 * @returns The reconstructed template string and the PhotoInterface array matching the slots
 */
export function reconstructTemplate(
  template: string,
  photos: PhotoInterface[]
): { template: string; photoInterfaces: PhotoInterface[] } {
  if (!template) return { template: '', photoInterfaces: [] };

  let reconstructed = template;
  const usedInterfaces: PhotoInterface[] = [];

  // Find all slot_N references and replace with actual photo data
  const slotPattern = /slot_(\d+) slot_\d+/g;
  let match: RegExpExecArray | null;
  const replacements: { original: string; replacement: string; index: number }[] = [];

  while ((match = slotPattern.exec(template)) !== null) {
    const slotIndex = parseInt(match[1], 10);
    if (slotIndex < photos.length) {
      const photo = photos[slotIndex];
      replacements.push({
        original: match[0],
        replacement: `${photo.uid} ${photo.photoId || ''}`,
        index: slotIndex
      });
      if (!usedInterfaces.includes(photo)) {
        usedInterfaces.push(photo);
      }
    }
  }

  // Apply replacements (reverse order to preserve indices)
  for (const rep of replacements) {
    reconstructed = reconstructed.replace(rep.original, rep.replacement);
  }

  return { template: reconstructed, photoInterfaces: usedInterfaces };
}

/**
 * Generate a unique composition ID
 */
export function generateCompositionId(): string {
  return `comp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Count the number of photo slots in a saved (slot-based) template.
 */
export function getSlotCount(template: string): number {
  if (!template) return 0;
  const matches = template.match(/slot_\d+ slot_\d+/g);
  return matches ? matches.length : 0;
}

/**
 * Create placeholder PhotoInterface objects for a saved template.
 * Parses the actual ratio and desiredSize from each leaf node so the engine
 * preserves the original layout.
 *
 * @param pageIndex  Album page index — ensures UIDs are unique across pages
 *                   to prevent cross-page collisions in photosMap and renderer cache.
 *
 * Leaf format in saved template: "ratio desiredSize slot_N slot_N"
 */
export function createPlaceholderInterfaces(template: string, pageIndex: number, target = 0): PhotoInterface[] {
  const slotPattern = /([\d.]+)\s+([\d.]+)\s+slot_(\d+)\s+slot_\d+/g;
  const interfaces: PhotoInterface[] = [];
  let match: RegExpExecArray | null;

  while ((match = slotPattern.exec(template)) !== null) {
    const ratio = parseFloat(match[1]) || 1.5;
    const desiredSize = parseFloat(match[2]) || 1;
    const index = parseInt(match[3], 10);
    const uid = `${COMPOSITION_PLACEHOLDER_PREFIX}p${pageIndex}_t${target}_s${index}`;

    interfaces[index] = {
      uid,
      ratio,
      desiredSize,
      photoId: uid
    };
  }

  // Fill any gaps (shouldn't happen, but defensive)
  return interfaces.filter(Boolean);
}

/**
 * Create a placeholder PhotoInfo object for a composition slot.
 * @param slotIndex  Slot index within the page
 * @param ratio      Photo aspect ratio (width/height) from the saved template leaf node.
 * @param pageIndex  Album page index — ensures md5 is unique across pages.
 */
export function createPlaceholderPhotoInfo(slotIndex: number, ratio = 1.5, pageIndex = 0, target = 0): PhotoInfo {
  const md5 = `${COMPOSITION_PLACEHOLDER_PREFIX}p${pageIndex}_t${target}_s${slotIndex}`;
  const photo = new PhotoInfo();
  photo.md5 = md5;
  photo.url = '';

  // Generate SVG at the correct photo dimensions (matches container aspect ratio)
  // so the renderer's crop won't clip the border
  let pw: number, ph: number;
  if (ratio >= 1) {
    pw = 300;
    ph = Math.round(300 / ratio);
  } else {
    pw = Math.round(200 * ratio);
    ph = 200;
  }
  photo.thumbnail = generatePlaceholderSvg(pw, ph);
  photo.width = pw;
  photo.height = ph;
  photo.id = 0;
  photo.name = `Placeholder ${slotIndex + 1}`;
  photo.rotation = 0;
  return photo;
}

/**
 * Check whether a photo element is a composition placeholder.
 */
export function isPlaceholderElement(element: any): boolean {
  return element?.photo?.md5?.startsWith(COMPOSITION_PLACEHOLDER_PREFIX) ?? false;
}

/**
 * Build a template string from a saved (slot-based) template with placeholder UIDs.
 * Replaces "slot_N slot_N" with page-unique placeholder UIDs.
 *
 * @param pageIndex  Album page index — ensures UIDs are unique across pages.
 */
export function buildPlaceholderTemplate(savedTemplate: string, pageIndex: number, target = 0): string {
  return savedTemplate.replace(
    /slot_(\d+) slot_\d+/g,
    (_, idx) => {
      const uid = `${COMPOSITION_PLACEHOLDER_PREFIX}p${pageIndex}_t${target}_s${idx}`;
      return `${uid} ${uid}`;
    }
  );
}

// ==================== Free-Form BSP Tree Generation ====================

type BSPNode = string | [string, [BSPNode, BSPNode]];

interface ElementRect {
  id: string;
  photoId: string;
  ratio: number;
  cx: number;
  cy: number;
}

/**
 * Build a BSP tree template string from manually-placed photo elements.
 * The output format matches the image engine's BSP tree:
 *   - Leaf: "ratio desiredSize uid photoId"
 *   - Branch: ["v"|"h", [leftChild, rightChild]]
 *
 * Algorithm: recursively partition elements by finding the largest
 * spatial gap along each axis and choosing the axis with the better split.
 */
export function buildBSPTreeFromElements(elements: PhotoElement[]): string {
  if (!elements || elements.length === 0) return '';

  const rects: ElementRect[] = elements.map(el => ({
    id: el.id,
    photoId: (el as any).photo?.md5 || '',
    ratio: el.width && el.height ? +(el.width / el.height).toFixed(4) : 1,
    cx: el.x + el.width / 2,
    cy: el.y + el.height / 2
  }));

  const tree = buildBSPNode(rects);
  return JSON.stringify(tree);
}

function buildBSPNode(rects: ElementRect[]): BSPNode {
  if (rects.length === 1) {
    const r = rects[0];
    return `${r.ratio} 1 ${r.id} ${r.photoId}`;
  }

  const vSplit = findLargestGap(rects, 'cx');
  const hSplit = findLargestGap(rects, 'cy');

  if (vSplit.gap >= hSplit.gap) {
    const sorted = [...rects].sort((a, b) => a.cx - b.cx);
    return ['v', [
      buildBSPNode(sorted.slice(0, vSplit.splitIndex)),
      buildBSPNode(sorted.slice(vSplit.splitIndex))
    ]];
  } else {
    const sorted = [...rects].sort((a, b) => a.cy - b.cy);
    return ['h', [
      buildBSPNode(sorted.slice(0, hSplit.splitIndex)),
      buildBSPNode(sorted.slice(hSplit.splitIndex))
    ]];
  }
}

function findLargestGap(
  rects: ElementRect[],
  axis: 'cx' | 'cy'
): { gap: number; splitIndex: number } {
  const sorted = [...rects].sort((a, b) => a[axis] - b[axis]);
  let maxGap = -1;
  let bestIndex = 1;

  for (let i = 1; i < sorted.length; i++) {
    const gap = sorted[i][axis] - sorted[i - 1][axis];
    if (gap > maxGap) {
      maxGap = gap;
      bestIndex = i;
    }
  }

  return { gap: maxGap, splitIndex: bestIndex };
}
